/**
 * Derivation of the legacy 4-character IAB US Privacy string from the GPP
 * USNAT (section 7) payload, per `services/specs/ads/us-privacy-signal-migration.md`
 * §5.
 *
 * Decision D1a assumed Google Privacy & messaging serves no `__uspapi`, making
 * this the only source of the legacy string on web. Measured on production
 * 2026-10-28, that is wrong: Google installs `__uspapi` itself and it answers
 * `1YNN`. Since `getUspConsentString` prefers `__uspapi` whenever it exists,
 * this derivation is effectively unreachable on web and is carried for iOS,
 * Android and the flint backstop, where no `__uspapi` exists. Revisit D1a.
 *
 * Contract highlights from the spec:
 * - Returns null when USNAT is not among the applicable sections. Never
 *   fabricate `1---`: that asserts "notice not applicable, not opted out",
 *   a claim we have not earned. Per-state sections (USCA etc.) are not USNAT
 *   and must not be mapped.
 * - Position 2 combines all three notice fields; a single 2 among them means
 *   the full notice was not given, so the honest answer is N.
 * - Position 3 is privacy-protective: any opt-out (1) wins, then any 2 is N,
 *   and only both-zero is `-`.
 * - Position 4 is always `N`. It describes Flipboard rather than the user, so
 *   it cannot vary by request. `N` is what every platform already sends: in an
 *   hour of flint logs, `1YN-` and `1YY-` appear nowhere, and Google's own
 *   `__uspapi` returns `1YNN`. See the spec's §5 for the measurements.
 * - Pure and synchronous; the identical mapping ships on iOS, Android and
 *   flint, pinned by the spec's §7 conformance vectors in each test suite.
 */

export const GPP_SECTION_USNAT = 7;

/**
 * Position 4, LSPA Covered Transaction. A statement about Flipboard's own
 * contractual status, not the user's, so it is a constant. `Y` would be a
 * false declaration unless we are an LSPA signatory *and* the transaction is
 * covered; `-` would assert "unknown" about something we know.
 */
const LSPA_COVERED = 'N';

export type UsnatFields = {
  SharingNotice?: number;
  SaleOptOutNotice?: number;
  TargetedAdvertisingOptOutNotice?: number;
  SaleOptOut?: number;
  SharingOptOut?: number;
};

const noticeChar = (fields: UsnatFields): string => {
  const notices = [
    fields.SharingNotice ?? 0,
    fields.SaleOptOutNotice ?? 0,
    fields.TargetedAdvertisingOptOutNotice ?? 0,
  ];
  if (notices.every((v) => v === 0)) {
    return '-';
  }
  if (notices.some((v) => v === 2)) {
    return 'N';
  }
  return 'Y';
};

const optOutChar = (fields: UsnatFields): string => {
  const optOuts = [fields.SaleOptOut ?? 0, fields.SharingOptOut ?? 0];
  if (optOuts.some((v) => v === 1)) {
    return 'Y';
  }
  if (optOuts.some((v) => v === 2)) {
    return 'N';
  }
  return '-';
};

export const deriveUsPrivacyString = (
  applicableSections: number[] | undefined,
  usnatFields: UsnatFields | null | undefined,
): string | null => {
  if (!applicableSections?.includes(GPP_SECTION_USNAT) || !usnatFields) {
    return null;
  }
  return `1${noticeChar(usnatFields)}${optOutChar(usnatFields)}${LSPA_COVERED}`;
};
